/**
 * usePsykerMoment - a power used, on a brother's life signs (Squad Mode, 2026-10-24): an attack he makes with a Force
 * or Warp weapon throws three seconds of spike trains (engine/plate/lifeSigns `psy`). A STAND-IN, by the user's ruling,
 * until psychic powers have a mark of their own - then this keys on the power (memory psyker-moment-is-a-stand-in).
 *
 * Read off the shell's live impacts (TargetsContext `impactsNow`, what just happened to each combatant): his own
 * attacks are the ones whose attacker is he. Never replayed (`usePassing`): the impacts a plate first sees already
 * happened.
 *
 * @param id - the brother whose plate this is
 * @returns the moments playing now
 */
import { useContext } from 'react';
import usePassing from './usePassing';
import TargetsContext from '../../contexts/TargetsContext';
import { hasWS } from '../../engine/weaponSpecials';
import type { Impact } from '../../engine/impacts';
import type { Moment } from '../../engine/plate/lifeSigns';

/** A psychic weapon: Force, or a Warp Weapon. */
const psychic = (specials: readonly string[]) => hasWS([...specials], 'Force') || hasWS([...specials], 'WarpWeapon');

/** His live psychic attacks, by impact id. */
export function psykerKey(impacts: Readonly<Record<string, Impact>> | null | undefined, id: string): string {
  return Object.values(impacts ?? {})
    .filter((i) => i.attackerId === id && psychic(i.specials ?? []))
    .map((i) => i.id)
    .sort()
    .join('|');
}

/** A moment: an attack of his that was not there before. */
function fresh(before: string, now: string): boolean {
  const had = new Set(before.split('|'));
  return !!now && now.split('|').some((k) => !had.has(k));
}

const NONE: Moment[] = [];
const PSY: Moment[] = ['psy'];

export default function usePsykerMoment(id: string): Moment[] {
  const targets = useContext(TargetsContext) as { impactsNow?: Record<string, Impact> } | null;
  return usePassing(psykerKey(targets?.impactsNow, id), fresh, 3000) ? PSY : NONE;
}
